var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './ImageFileCrop.scss';
import { Slider } from 'antd';
import { useOnUnmount } from '@hooks';
import { createCroppedImage, readDataFromFile } from '@sonar-software/sonar-ts-lib';
import Cropper from 'react-easy-crop';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { RotateLeftOutlined, RotateRightOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
const defaultCrop = {
    x: 0,
    y: 0
};
const defaultCropSize = {
    height: 0,
    width: 0
};
const defaultArea = {
    height: 0,
    width: 0,
    x: 0,
    y: 0
};
const defaultRotation = 0;
const minZoom = 1;
const maxZoom = 5;
const minRotate = -180;
const maxRotate = 180;
export const ImageFileCrop = forwardRef(({ aspect = 1, cropShape = 'rect', sourceFile }, ref) => {
    const [crop, setCrop] = useState(defaultCrop);
    const [cropSize, setCropSize] = useState(defaultCropSize);
    const [rotation, setRotation] = useState(defaultRotation);
    const [zoom, setZoom] = useState(minZoom);
    const [image, setImage] = useState();
    const imageTypeRef = useRef();
    const cropAreaRef = useRef(defaultArea);
    useEffect(() => {
        function processFile() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!sourceFile) {
                    return;
                }
                const imageData = yield readDataFromFile(sourceFile);
                if (typeof imageData === 'string') {
                    imageTypeRef.current = sourceFile.type;
                    setImage(imageData);
                }
            });
        }
        processFile();
    }, [sourceFile]);
    useOnUnmount(() => {
        if (image) {
            URL.revokeObjectURL(image);
        }
    });
    useImperativeHandle(ref, () => ({
        getCroppedImage: () => {
            if (image) {
                return createCroppedImage(image, imageTypeRef.current, cropAreaRef.current, rotation);
            }
            return Promise.resolve(null);
        }
    }), [image, rotation]);
    const handleMediaLoaded = useCallback((mediaSize) => {
        const { width, height } = mediaSize;
        const ratioWidth = height * aspect;
        if (width > ratioWidth) {
            setCropSize({
                height,
                width: ratioWidth
            });
        }
        else {
            setCropSize({
                height: width / aspect,
                width
            });
        }
    }, [aspect]);
    const handleCropComplete = useCallback((_, cropAreaPixels) => {
        cropAreaRef.current = cropAreaPixels;
    }, []);
    return (React.createElement("section", { className: 'image-file-crop' },
        React.createElement("div", { className: 'image-file-crop-container' },
            React.createElement(Cropper, { aspect: aspect, crop: crop, cropShape: cropShape, cropSize: cropSize, image: image, maxZoom: maxZoom, minZoom: minZoom, onCropChange: setCrop, onCropComplete: handleCropComplete, onMediaLoaded: handleMediaLoaded, onRotationChange: setRotation, onZoomChange: setZoom, rotation: rotation, zoom: zoom })),
        React.createElement("div", { className: 'image-file-crop-controls' },
            React.createElement("div", null,
                React.createElement(ZoomOutOutlined, null),
                React.createElement(Slider, { defaultValue: minZoom, max: maxZoom, min: minZoom, onChange: setZoom, step: 0.1, value: zoom }),
                React.createElement(ZoomInOutlined, null)),
            React.createElement("div", null,
                React.createElement(RotateLeftOutlined, null),
                React.createElement(Slider, { defaultValue: 0, max: maxRotate, min: minRotate, onChange: setRotation, step: 1, value: rotation }),
                React.createElement(RotateRightOutlined, null)))));
});
